import React, { useEffect, useRef, useState } from 'react';

import { isEqual } from 'lodash-es';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { FormValidationState } from 'utils';

import { ILocation } from '@rbi-ctg/frontend';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { getPhoneNumberErrorMessage, isPhoneNumberValid } from 'utils/form';
import { IPlaceAddress } from 'utils/geolocation';

import { ModalContent } from '../styled';
import { IDeliveryAddressFormProps, IPhoneAddressValidation } from '../types';

import DeliveryAddressFormAdditionalFields from './delivery-address-form-additional-fields';
import DeliveryAddressFormFooter from './delivery-address-form-footer';
import DeliveryAddressFormHeader from './delivery-address-form-header';
import DeliveryAddressFormInput from './delivery-address-form-input';

const StyledForm = styled.form`
  margin-bottom: 0.5rem;
  width: 100%;
`;

export const isCompleteAddress = (address: IPlaceAddress): boolean => {
  const { addressLine1, city, state, zip } = address;
  return !!(addressLine1 && city && state && zip);
};

const DeliveryAddressForm: React.FC<IDeliveryAddressFormProps> = ({
  instructions,
  user,
  setCreateAddressView,
  getDetailsPlaceData,
  showDeliveryAddresses,
  setAddress,
}) => {
  const { formatMessage } = useIntl();
  const unitRef = useRef<HTMLInputElement>(null);
  const { logNavigationClick } = useMParticleContext();

  const [message, setMessage] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [validation, setValidation] = useState<IPhoneAddressValidation>({
    phoneNumber: FormValidationState.PRISTINE,
    address: FormValidationState.PRISTINE,
  });
  const [addressComponents, setAddressComponents] = useState<IPlaceAddress | null>(null);
  const [coordinates, setCoordinates] = useState<ILocation | undefined>();

  const [addressString, setAddressString] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(user?.details?.phoneNumber || '');
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');
  const [deliveryInstructions, setDeliveryInstructions] = useState(instructions || '');
  const [shouldSaveAddress, setShouldSaveAddress] = useState(false);

  const enableSimplifiedDelivery = useFlag(LaunchDarklyFlag.ENABLE_SIMPLE_DELIVERY_FORM);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const phoneNumberValidation = await isPhoneNumberValid(phoneNumber);

    if (!phoneNumberValidation.valid && !enableSimplifiedDelivery) {
      setValidation({ ...validation, phoneNumber: FormValidationState.INVALID });
      setPhoneNumberError(getPhoneNumberErrorMessage(phoneNumberValidation.error, formatMessage));
      logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
        error: 'Invalid Phone Number',
      });
      return;
    }

    setPhoneNumberError('');
    if (!addressComponents || validation.address === FormValidationState.INVALID) {
      setMessage(formatMessage({ id: 'addressError' }));
      logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
        error: 'Invalid Address',
      });
      return;
    }

    setValidation({ ...validation, address: FormValidationState.VALID });
    setMessage('');

    // track attempt to deliver
    logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE);
    setAddress({
      address: addressComponents,
      deliveryInstructions,
      phoneNumber,
      shouldSaveAddress,
      coordinates,
      placeId,
    });
  };

  useEffect(() => {
    if (!placeId) {
      return;
    }

    getDetailsPlaceData(placeId).then(placeData => {
      if (!isCompleteAddress(placeData.address)) {
        setValidation(prevValidation => ({
          ...prevValidation,
          address: FormValidationState.INVALID,
        }));
        setMessage(formatMessage({ id: 'addressError' }));
        return;
      }

      if (!isEqual(placeData.address, addressComponents)) {
        setDeliveryInstructions('');
      }

      setCoordinates(placeData.coordinates);
      setAddressComponents(placeData.address);
    });
  }, [formatMessage, getDetailsPlaceData, placeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const FlaggedContainer = enableSimplifiedDelivery ? ModalContent : React.Fragment;

  return (
    <FlaggedContainer>
      <DeliveryAddressFormHeader />
      <StyledForm onSubmit={handleSubmit}>
        <DeliveryAddressFormInput
          setAddressString={setAddressString}
          setPlaceId={setPlaceId}
          setValidation={setValidation}
          setMessage={setMessage}
          unitRef={unitRef}
          validation={validation}
          setCoordinates={setCoordinates}
          addressString={addressString}
          message={message}
        />
        {!enableSimplifiedDelivery && (
          <DeliveryAddressFormAdditionalFields
            addressComponents={addressComponents}
            setPhoneNumber={setPhoneNumber}
            setAddressComponents={setAddressComponents}
            deliveryInstructions={deliveryInstructions}
            setDeliveryInstructions={setDeliveryInstructions}
            user={user}
            validation={validation}
            phoneNumberError={phoneNumberError}
            unitRef={unitRef}
            shouldSaveAddress={shouldSaveAddress}
            setShouldSaveAddress={setShouldSaveAddress}
            phoneNumber={phoneNumber}
          />
        )}
        <DeliveryAddressFormFooter
          setCreateAddressView={setCreateAddressView}
          showDeliveryAddresses={showDeliveryAddresses}
          addressString={addressString}
        />
      </StyledForm>
    </FlaggedContainer>
  );
};

export default DeliveryAddressForm;
